<script lang="ts">
    import SaveSavedIcon from "../../../svelte/icons/saveSavedIcon.svelte";
    import SaveEditedIcon from "../../../svelte/icons/saveEditedIcon.svelte";
    import { Switch } from "@/components/ui/switch";
    import { Button } from "@/components/ui/button";
    import type { AutosaveStatus } from "../autosave.svelte";

    type Props = {
        readonlyPlanner: boolean;
        modifiedCount: number;
        onSaveClick: () => void;
        autosaveEnabled: boolean;
        autosaveStatus: AutosaveStatus;
        skippedAutosaveCount: number;
    };

    let {
        readonlyPlanner,
        modifiedCount,
        onSaveClick,
        autosaveEnabled = $bindable(),
        autosaveStatus,
        skippedAutosaveCount,
    }: Props = $props();
</script>

<div class="flex flex-row justify-between gap-2">
    {#if !readonlyPlanner}
        <div class="flex items-center gap-2">
            <Button
                variant="outline"
                size="sm"
                onclick={(e) => {
                    e.stopPropagation();
                    onSaveClick();
                }}
                class="flex h-7 items-center gap-2 text-xs"
            >
                {#if modifiedCount === 0}
                    <SaveSavedIcon/>
                {:else}
                    <SaveEditedIcon/>
                {/if}
                Save
            </Button>
            <div class="autosave-controls">
                <label class="autosave-toggle">
                    <Switch
                        bind:checked={autosaveEnabled}
                        class={autosaveEnabled
                            ? "data-[state=checked]:bg-[var(--success)]"
                            : "data-[state=unchecked]:bg-[var(--error)]"
                        }
                    />
                    <span class="text-xs text-center leading-tight">Auto<br/>Save</span>
                </label>
                {#if autosaveEnabled}
                    <span class="autosave-status" title={
                        autosaveStatus === 'idle' ? 'Autosave ready' :
                        autosaveStatus === 'pending' ? 'Waiting to save...' :
                        autosaveStatus === 'saving' ? 'Saving...' :
                        autosaveStatus === 'saved' ? 'Saved!' :
                        'Autosave error'
                    }>
                        {#if autosaveStatus === 'idle'}
                            <span class="status-dot idle"></span>
                        {:else if autosaveStatus === 'pending'}
                            <span class="status-dot pending"></span>
                        {:else if autosaveStatus === 'saving'}
                            <span class="status-dot saving"></span>
                        {:else if autosaveStatus === 'saved'}
                            <span class="status-dot saved"></span>
                        {:else if autosaveStatus === 'error'}
                            <span class="status-dot error"></span>
                        {/if}
                    </span>
                    {#if skippedAutosaveCount > 0}
                        <span class="text-xs text-amber-600" title="Items with missing required fields cannot be autosaved">
                            ({skippedAutosaveCount} skipped)
                        </span>
                    {/if}
                {/if}
            </div>
        </div>
    {:else}
        <div></div>
    {/if}
</div>

<style lang="scss">
    .autosave-controls {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .autosave-toggle {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.25rem;
        cursor: pointer;
    }

    .autosave-status {
        display: flex;
        align-items: center;
    }

    .status-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        display: inline-block;

        &.idle {
            background-color: var(--muted-foreground);
        }

        &.pending {
            background-color: var(--warning, hsl(45 93% 47%));
            animation: pulse 1.5s ease-in-out infinite;
        }

        &.saving {
            background-color: var(--primary);
            animation: pulse 0.5s ease-in-out infinite;
        }

        &.saved {
            background-color: var(--success);
        }

        &.error {
            background-color: var(--error);
        }
    }

    @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.4; }
    }
</style>